import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Printer,
  BarChart3,
  Lock,
  PieChart as PieIcon,
  Layers,
  Trophy,
  SlidersHorizontal,
  Crosshair,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportHeader } from '@/components/report/report-header';
import { ReportKpis } from '@/components/report/report-kpis';
import { ReportSegments } from '@/components/report/report-segments';
import { ReportSkuTable } from '@/components/report/report-sku-table';
import { ReportAutoCompare } from '@/components/report/report-auto-compare';
import { ReportTrendChart } from '@/components/report/report-trend-chart';
import { ReportTopSkusChart } from '@/components/report/report-top-skus-chart';
import { ReportCategoryDonut } from '@/components/reports/report-category-donut';
import { ReportBrandDonut } from '@/components/reports/report-brand-donut';
import { ReportSpecBreakdown } from '@/components/reports/report-spec-breakdown';
import { ReportTopSkusTable } from '@/components/reports/report-top-skus-table';
import { ReportMarketShare } from '@/components/reports/report-market-share';
import { useCurrentDate } from '@/hooks/use-current-date';
import { cn } from '@/lib/utils';
import { getSharedReport, isAllBrand, TEMPLATE_DEFAULTS } from '@/lib/api';
import type { SharedReport } from '@/lib/api';

// Partner-side fallback when share is brand-only legacy (no report.* block):
// use 'partner' template defaults — most restrictive, safest for external audience.
const FALLBACK_OPTIONS = TEMPLATE_DEFAULTS.partner;

const FOOTER_DATE = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

const EXPIRES_FMT = new Intl.DateTimeFormat('vi-VN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Asia/Ho_Chi_Minh',
});

function formatMonthRange(months: string[]): string | undefined {
  if (months.length < 2) return months[0];
  const first = months[0];
  const last = months[months.length - 1];
  if (!first || !last) return undefined;
  const fmt = (m: string) => {
    const [y, mo] = m.split('-');
    return `${mo}/${y}`;
  };
  return `${fmt(first)} → ${fmt(last)}`;
}

export default function SharedReportRoute() {
  const { token } = useParams<{ token: string }>();
  const [report, setReport] = useState<SharedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const now = useCurrentDate();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    getSharedReport(token)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        document.title = data.report?.title
          ? `${data.report.title} — GearVN`
          : `Báo cáo ${data.brand} — GearVN`;
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      document.title = 'Monitor Dashboard — GearVN';
    };
  }, [token]);

  const focalSkus = useMemo(
    () =>
      (report?.monitors ?? [])
        .filter((m) => m.is_focal === 1)
        .sort((a, b) => a.name.localeCompare(b.name, 'vi')),
    [report]
  );

  const allSkus = useMemo(
    () =>
      [...(report?.monitors ?? [])].sort((a, b) => {
        if (a.is_focal !== b.is_focal) return b.is_focal - a.is_focal;
        return a.name.localeCompare(b.name, 'vi');
      }),
    [report]
  );

  const monthRange = useMemo(
    () => (report ? formatMonthRange(report.months) : undefined),
    [report]
  );

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <div className="flex items-end justify-between border-b pb-4">
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-7 w-72" />
            <Skeleton className="h-3 w-40" />
          </div>
          <Skeleton className="h-12 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <Lock className="h-6 w-6" />
          </div>
          <h1 className="mt-4 text-lg font-semibold">Link không khả dụng</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error ?? 'Link chia sẻ này đã hết hạn hoặc đã bị thu hồi.'}
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Vui lòng liên hệ GearVN để được cấp link mới.
          </p>
        </div>
      </div>
    );
  }

  const opts = report.report?.display_options ?? FALLBACK_OPTIONS;
  const allBrandView = isAllBrand(report.brand);
  const hasMarketContext = !allBrandView && report.market_total && report.market_segments;

  return (
    <div className="relative mx-auto max-w-5xl">
      <div className="print-watermark hidden">CONFIDENTIAL</div>

      {/* Top action bar — only Print + share info, no nav */}
      <div className="sticky top-0 z-20 flex flex-col gap-2 border-b bg-background/80 px-6 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between print:hidden">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" />
          <span>
            Link chia sẻ —{' '}
            {report.share.expires_at
              ? `hết hạn ${EXPIRES_FMT.format(new Date(report.share.expires_at))}`
              : 'không hết hạn'}
          </span>
        </div>
        <Button size="sm" onClick={handlePrint}>
          <Printer className="h-4 w-4" />
          In / Xuất PDF
        </Button>
      </div>

      <div className="report-typo-scale space-y-6 p-6">
        {report.report && (
          <div className="space-y-1 rounded-lg border border-primary/20 bg-primary/5 p-4 print:rounded-md">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">
              Snapshot Report
            </div>
            <h1 className="text-lg font-bold tracking-tight">{report.report.title}</h1>
            {report.report.description && (
              <p className="text-xs text-muted-foreground">{report.report.description}</p>
            )}
          </div>
        )}

        {/* Intro text from admin — plain text, line breaks preserved */}
        {report.report?.intro_text && (
          <div className="rounded-lg border-l-4 border-primary/40 bg-primary/5 px-4 py-3 text-sm leading-relaxed">
            <p className="whitespace-pre-line text-foreground">{report.report.intro_text}</p>
          </div>
        )}

        <ReportHeader brand={report.brand} monthRange={monthRange} date={now} />

        <ReportKpis
          totals={report.totals}
          periodLabel={report.period.label}
          compare={
            report.compare
              ? { totals: report.compare.totals, periodLabel: report.compare.period.label }
              : null
          }
          hideMarketShare={allBrandView}
        />

        {/* Vị thế trong Ngành Monitor — single-brand reports only.
            Phase 4: so sánh brand vs toàn ngành (revenue/units + per-segment share). */}
        {hasMarketContext && (
          <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
            <div className="mb-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Crosshair className="h-4 w-4 text-primary" />
                Vị thế trong Ngành Monitor
              </h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {report.brand} vs Toàn ngành Monitor — so sánh quy mô + thị phần per segment
              </p>
            </div>
            <ReportMarketShare
              brandName={report.brand}
              brandTotal={{
                revenue_vnd: report.totals.revenue_vnd,
                units_sold: report.totals.units_sold,
              }}
              marketTotal={report.market_total!}
              brandSegments={report.segments}
              marketSegments={report.market_segments!}
            />
          </section>
        )}

        {/* Trend chart — keep legacy partner-friendly Area chart (cleaner than dual-axis mixed). */}
        <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <BarChart3 className="h-4 w-4 text-primary" />
                {report.months.length === 1
                  ? `Top SKU theo doanh thu — ${report.period.label}`
                  : 'Xu hướng Doanh thu & Lợi nhuận'}
              </h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {report.months.length === 1
                  ? `${report.brand} · sắp xếp doanh thu giảm dần`
                  : `${report.months.length} tháng · ${report.brand}`}
              </p>
            </div>
          </div>
          {report.months.length === 1 ? (
            <ReportTopSkusChart monitors={report.monitors} />
          ) : (
            <ReportTrendChart data={report.trend} />
          )}
        </section>

        {/* Auto-compare blocks (QoQ + MoM) — legacy brand-only shares. Report-based shares
            đã có frozen compare trong KPIs ở trên — auto_compare luôn null. */}
        {report.auto_compare && <ReportAutoCompare data={report.auto_compare} />}

        {/* Cơ cấu Segment + (optional) Cơ cấu theo Hãng — layout phụ thuộc context:
            - Single-brand + single-donut (partner default): merge donut + segment cards vào 1 section,
              donut bên trái (cố định 280px) + cards bên phải (1fr) → tận dụng full-width, không bị giãn.
            - Single-brand + 2-donut: 2 donuts side-by-side, segment cards là section riêng bên dưới.
            - All-brand: 2 sections (Segment + Brand donut) side-by-side, segment cards bên dưới. */}
        {(() => {
          const showMergedLayout = !allBrandView && !opts.show_donut_units;
          return (
            <>
              <div className={cn('grid gap-4', allBrandView && 'lg:grid-cols-2')}>
                <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                  <div className="mb-3">
                    <h2 className="flex items-center gap-2 text-sm font-semibold">
                      <PieIcon className="h-4 w-4 text-primary" />
                      Cơ cấu Segment
                    </h2>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {report.brand} · {showMergedLayout ? 'tỷ trọng + chi tiết theo phân khúc' : 'so sánh tỷ trọng theo Doanh thu vs Số lượng'}
                    </p>
                  </div>

                  {showMergedLayout ? (
                    /* Merged layout: donut left (280px), segment cards right (1fr) */
                    <div className="grid gap-6 lg:grid-cols-[280px_1fr] lg:items-start">
                      <ReportCategoryDonut segments={report.segments} metric="revenue" />
                      <ReportSegments segments={report.segments} />
                    </div>
                  ) : (
                    /* Standard layout: 1-2 donuts depending on show_donut_units */
                    <div className={cn(
                      'grid gap-6',
                      opts.show_donut_units && (allBrandView ? 'xl:grid-cols-2' : 'md:grid-cols-2')
                    )}>
                      <div className="space-y-2">
                        <div className="text-center text-xs font-semibold text-muted-foreground">
                          Theo Doanh thu
                        </div>
                        <ReportCategoryDonut segments={report.segments} metric="revenue" />
                      </div>
                      {opts.show_donut_units && (
                        <div className={cn(
                          'space-y-2 border-t pt-4',
                          allBrandView
                            ? 'xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0'
                            : 'md:border-l md:border-t-0 md:pl-6 md:pt-0'
                        )}>
                          <div className="text-center text-xs font-semibold text-muted-foreground">
                            Theo Số lượng bán
                          </div>
                          <ReportCategoryDonut segments={report.segments} metric="units" />
                        </div>
                      )}
                    </div>
                  )}
                </section>

                {allBrandView && (
                  <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
                    <div className="mb-3">
                      <h2 className="flex items-center gap-2 text-sm font-semibold">
                        <Layers className="h-4 w-4 text-primary" />
                        Cơ cấu theo Hãng
                      </h2>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        Toàn ngành Monitor · so sánh tỷ trọng theo Doanh thu vs Số lượng
                      </p>
                    </div>
                    <div className={cn('grid gap-6', opts.show_donut_units && 'xl:grid-cols-2')}>
                      <div className="space-y-2">
                        <div className="text-center text-xs font-semibold text-muted-foreground">
                          Theo Doanh thu
                        </div>
                        <ReportBrandDonut monitors={report.monitors} metric="revenue" idPrefix="share-brand-rev" />
                      </div>
                      {opts.show_donut_units && (
                        <div className="space-y-2 border-t pt-4 xl:border-l xl:border-t-0 xl:pl-6 xl:pt-0">
                          <div className="text-center text-xs font-semibold text-muted-foreground">
                            Theo Số lượng bán
                          </div>
                          <ReportBrandDonut monitors={report.monitors} metric="units" idPrefix="share-brand-units" />
                        </div>
                      )}
                    </div>
                  </section>
                )}
              </div>

              {/* Standalone segment cards — only when NOT merged into donut section above */}
              {!showMergedLayout && <ReportSegments segments={report.segments} />}
            </>
          );
        })()}

        {/* Spec breakdown — single-brand only (all-brand đã có Cơ cấu theo Hãng) */}
        {!allBrandView && (
          <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none print-no-break">
            <div className="mb-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <SlidersHorizontal className="h-4 w-4 text-primary" />
                Phân khúc theo Spec
              </h2>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                Breakdown theo kích thước · độ phân giải · tần số quét
              </p>
            </div>
            <ReportSpecBreakdown monitors={report.monitors} />
          </section>
        )}

        {/* Top 15 SKU bán tốt nhất */}
        <section className="rounded-lg border bg-card p-4 shadow-sm print:rounded-md print:shadow-none">
          <div className="mb-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Trophy className="h-4 w-4 text-primary" />
              Top 15 SKU bán tốt nhất
            </h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Ranking theo số lượng / doanh thu · top 3 highlight vàng
            </p>
          </div>
          <ReportTopSkusTable
            monitors={report.monitors}
            maxRows={15}
            showInternalColumns={opts.show_internal_columns}
          />
        </section>

        <div className="print-break-before pt-2">
          <ReportSkuTable
            title="Sản phẩm Tập trung (Focal Point)"
            monitors={focalSkus}
            showInternalColumns={opts.show_internal_columns}
          />
        </div>

        {opts.show_all_skus && (
          <ReportSkuTable
            title="Toàn bộ SKU Active"
            monitors={allSkus}
            showInternalColumns={opts.show_internal_columns}
          />
        )}

        <footer className="flex items-center justify-between border-t pt-3 text-[10px] text-muted-foreground">
          <span>
            GearVN × {report.brand} — Confidential | {FOOTER_DATE.format(now)}
          </span>
          <span>{report.total_skus} SKUs · {report.focal_count} focal</span>
        </footer>
      </div>
    </div>
  );
}
